import React, {useEffect, ReactElement} from 'react';
import {useBoolean} from 'src/hooks/useBoolean';
import {Formik, useFormikContext} from 'formik';
import tableStyles from 'src/components/SimpleTable/body.module.scss';
import Button from 'src/components/button';
import classnames from 'classnames';
import styles from './styles.module.scss';
import LoadingIcon from 'shared/components/loading';
import {CompleteColumnSettings, ItemValue, PersistedItemValue} from './types';
import {buildApiError} from 'src/utils/errors';
import {FormFieldError} from 'src/utils/errors/FormFieldError';
import Yup from '../../../utils/yup';

interface FormParams {
  showButton?: boolean | string;
  showClearButton?: boolean;
  createOrUpdate: (item: ItemValue | PersistedItemValue) => Promise<void>;
  afterSubmit?: (saved: boolean) => void;
  columns: CompleteColumnSettings[];
  renderNonEditableFields: boolean;
  apiFieldMap?: Record<string, string>;
  bulkSave?: boolean;
  inContainer?: boolean;
  formSchema?: Yup.ObjectSchema;
}

const FormComponent = (props: FormParams) => {
  const saving = useBoolean(false);
  const form = useFormikContext<ItemValue>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {formSchema} = props;

  const save = (): void => {
    if (saving.value) {
      return;
    }

    if (!form.dirty) {
      props.afterSubmit && props.afterSubmit(false);
      return;
    }

    saving.setTrue();
    props
      .createOrUpdate(form.values)
      .then(() => {
        saving.setFalse();
        form.resetForm();
        props.afterSubmit && props.afterSubmit(true);
      })
      .catch((errors) => {
        const formFieldError = buildApiError(FormFieldError, errors) as FormFieldError;
        if (formFieldError) {
          form.setErrors(formFieldError.getMappedFieldErrors(props.apiFieldMap));
        }
        saving.setFalse();
      });
  };

  const closeForm = (): void => {
    form.resetForm();
    saving.setFalse();
    props.afterSubmit && props.afterSubmit(false);
  };

  useEffect(() => {
    props.bulkSave && save();
  }, [props.bulkSave]);

  const isCreating = !form.values['id'];
  const filteredColumns = props.renderNonEditableFields
    ? props.columns
    : props.columns.filter((c) => c.editable || (isCreating && c.editableOnCreate));

  return (
    <tr
      className={classnames('editable-table-form', {[tableStyles.tr]: !!form.values['id']})}
      data-testid="editable-table-form"
    >
      {filteredColumns.map((column, i) => {
        const lastItem = i === filteredColumns.length - 1;
        const FormField = column.FormField;
        const isFieldEditable =
          column.editable || (!props.renderNonEditableFields && column.editableOnCreate);

        return (
          <td
            key={column.name}
            style={column.width ? {width: column.width} : {}}
            className={classnames('editable-table-form-td', 'text-center', styles.formCell, {
              [props.inContainer ? tableStyles.inContainerTd : tableStyles.td]: lastItem,
              [styles.td]: lastItem,
            })}
          >
            {isFieldEditable ? (
              <FormField column={column} />
            ) : (
              // @ts-expect-error
              column.renderValue(form.values[column.name], form.values)
            )}
            {lastItem && !props.showButton && saving.value && (
              <LoadingIcon className={styles.rightIcon} />
            )}
          </td>
        );
      })}
      {props.showButton && (
        <div
          className={classnames(
            'editable-table-form-button',
            'text-center',
            props.inContainer ? styles.actionsCompact : styles.actions
          )}
        >
          {saving.value ? (
            <LoadingIcon />
          ) : (
            <div className={styles.buttonGroup}>
              <Button margin={false} onClick={save} disabled={!form.isValid || saving.value}>
                {typeof props.showButton === 'string' ? props.showButton : 'Add'}
              </Button>
              {props.showClearButton && (
                <Button margin={false} onClick={closeForm}>
                  {form.dirty ? 'Clear' : 'Close'}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </tr>
  );
};

function Form(props: FormParams & {values: ItemValue}): ReactElement {
  return (
    <Formik
      initialValues={props.values}
      onSubmit={props.createOrUpdate}
      validationSchema={props.formSchema}
    >
      <FormComponent {...props} />
    </Formik>
  );
}

export default Form;
